import {
  Calendar,
  MapPin,
  Navigation,
  Plane,
  Route,
  Save,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { CountryAutocomplete } from "../../components/ui/CountryAutocomplete";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  createSegment,
  getSegment,
  updateSegment,
} from "../../services/api/travels";
import { useI18nStore } from "../../stores/i18nStore";

export const SegmentFormPage: React.FC = () => {
  const { travelId, segmentId } = useParams<{
    travelId: string;
    segmentId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const isEditMode = segmentId !== "new";

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    segmentTitle: "",
    segmentType: "destination" as const,
    segmentOperator: "",
    bookingReference: "",
    startDateTime: "",
    startLocation: "",
    startCountry: "",
    endDateTime: "",
    endLocation: "",
    endCountry: "",
    transportationType: "" as string,
    accommodationType: "" as string,
    notes: "",
  });

  useEffect(() => {
    if (isEditMode && travelId && segmentId) {
      loadSegment(travelId, segmentId);
    }
  }, [travelId, segmentId, isEditMode]);

  const loadSegment = async (tId: string, sId: string) => {
    try {
      setLoading(true);
      const segment = await getSegment(tId, sId);
      setFormData({
        segmentTitle: segment.segmentTitle || "",
        segmentType:
          (segment.segmentType as typeof formData.segmentType) || "destination",
        segmentOperator: segment.segmentOperator || "",
        bookingReference: segment.bookingReference || "",
        startDateTime: segment.startDateTime
          ? segment.startDateTime.substring(0, 16)
          : "",
        startLocation: segment.startLocation || "",
        startCountry: segment.startCountry || "",
        endDateTime: segment.endDateTime
          ? segment.endDateTime.substring(0, 16)
          : "",
        endLocation: segment.endLocation || "",
        endCountry: segment.endCountry || "",
        transportationType: segment.transportationType || "",
        accommodationType: segment.accommodationType || "",
        notes: segment.notes || "",
      });
    } catch (error: any) {
      console.error("Failed to load segment:", error);
      toast.error("Failed to load segment details");
      navigate(`/travels/${travelId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.segmentTitle.trim()) {
      toast.error("Segment title is required");
      return;
    }
    if (!formData.startLocation.trim()) {
      toast.error("Start location is required");
      return;
    }
    if (!formData.endLocation.trim()) {
      toast.error("End location is required");
      return;
    }
    if (!formData.startDateTime) {
      toast.error("Start date/time is required");
      return;
    }
    if (!formData.endDateTime) {
      toast.error("End date/time is required");
      return;
    }

    // Date validation
    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);
    if (end < start) {
      toast.error("End date/time must be after start date/time");
      return;
    }

    if (!travelId) {
      toast.error("Travel ID missing");
      return;
    }

    try {
      setSaving(true);
      const segmentData = {
        segmentTitle: formData.segmentTitle,
        segmentType: formData.segmentType,
        segmentOperator: formData.segmentOperator,
        bookingReference: formData.bookingReference,
        startDateTime: new Date(formData.startDateTime).toISOString(),
        startLocation: formData.startLocation,
        startCountry: formData.startCountry,
        endDateTime: new Date(formData.endDateTime).toISOString(),
        endLocation: formData.endLocation,
        endCountry: formData.endCountry,
        transportationType: formData.transportationType,
        accommodationType: formData.accommodationType,
        notes: formData.notes,
      };

      if (isEditMode && segmentId) {
        await updateSegment(travelId, segmentId, segmentData);
        toast.success("Segment updated successfully");
      } else {
        await createSegment(travelId, segmentData);
        toast.success("Segment created successfully");
      }
      navigate(`/travels/${travelId}`);
    } catch (error: any) {
      console.error("Failed to save segment:", error);
      toast.error("Failed to save segment");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/traveler/travels/${travelId}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <PageBanner
          icon={Route}
          title={t.tripSegments || "Trip Segments"}
          description="Manage travel segments"
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={Route}
        title={
          isEditMode
            ? t.editSegment || "Edit Segment"
            : t.addSegment || "Add Segment"
        }
        description={
          isEditMode
            ? "Update segment details"
            : "Add a new segment to your trip"
        }
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Navigation className="h-5 w-5 text-purple-600" />
              {t.segmentInformation || "Segment Information"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.segmentTitle || "Segment Title"} *
                </label>
                <input
                  type="text"
                  value={formData.segmentTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, segmentTitle: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Flight to Barcelona"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.segmentType || "Segment Type"} *
                </label>
                <select
                  value={formData.segmentType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      segmentType: e.target
                        .value as typeof formData.segmentType,
                    })
                  }
                  className="w-full h-[42px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                  required
                >
                  <option value="transfer">
                    {t.segmentTypeTransfer || "Transfer"}
                  </option>
                  <option value="destination">
                    {t.segmentTypeDestination || "Destination"}
                  </option>
                  <option value="accommodation">
                    {t.segmentTypeAccommodation || "Accommodation"}
                  </option>
                  <option value="activity">
                    {t.segmentTypeActivity || "Activity"}
                  </option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.operator || "Operator"}
                </label>
                <input
                  type="text"
                  value={formData.segmentOperator}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      segmentOperator: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Lufthansa, Marriott"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.bookingReference || "Booking Reference"}
                </label>
                <input
                  type="text"
                  value={formData.bookingReference}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bookingReference: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., ABC123"
                />
              </div>
            </div>
          </div>

          {/* Locations */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-purple-600" />
              {t.locations || "Locations"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.startLocation || "Start Location"} *
                </label>
                <input
                  type="text"
                  value={formData.startLocation}
                  onChange={(e) =>
                    setFormData({ ...formData, startLocation: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Munich Airport"
                  required
                />
              </div>
              <CountryAutocomplete
                value={formData.startCountry}
                onChange={(value) =>
                  setFormData({ ...formData, startCountry: value })
                }
                label={t.startCountry || "Start Country"}
                placeholder="e.g., Germany"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.endLocation || "End Location"} *
                </label>
                <input
                  type="text"
                  value={formData.endLocation}
                  onChange={(e) =>
                    setFormData({ ...formData, endLocation: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Barcelona Airport"
                  required
                />
              </div>
              <CountryAutocomplete
                value={formData.endCountry}
                onChange={(value) =>
                  setFormData({ ...formData, endCountry: value })
                }
                label={t.endCountry || "End Country"}
                placeholder="e.g., Spain"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              {t.dateTime || "Date & Time"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.startDateTime || "Start Date & Time"} *
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDateTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startDateTime: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.endDateTime || "End Date & Time"} *
                </label>
                <input
                  type="datetime-local"
                  value={formData.endDateTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endDateTime: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Transportation & Accommodation */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Plane className="h-5 w-5 text-purple-600" />
              {t.travelDetails || "Travel Details"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.transportation || "Transportation"}
                </label>
                <select
                  value={formData.transportationType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transportationType: e.target.value,
                    })
                  }
                  className="w-full h-[42px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">{t.selectOption || "Select..."}</option>
                  <option value="flight">
                    {t.transportFlight || "Flight"}
                  </option>
                  <option value="train">{t.transportTrain || "Train"}</option>
                  <option value="bus">{t.transportBus || "Bus"}</option>
                  <option value="car">{t.transportCar || "Car"}</option>
                  <option value="ship">{t.transportShip || "Ship"}</option>
                  <option value="other">{t.purposeOther || "Other"}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.accommodation || "Accommodation"}
                </label>
                <select
                  value={formData.accommodationType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      accommodationType: e.target.value,
                    })
                  }
                  className="w-full h-[42px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">{t.selectOption || "Select..."}</option>
                  <option value="hotel">
                    {t.accommodationHotel || "Hotel"}
                  </option>
                  <option value="hostel">
                    {t.accommodationHostel || "Hostel"}
                  </option>
                  <option value="apartment">
                    {t.accommodationApartment || "Apartment"}
                  </option>
                  <option value="cruise_cabin">
                    {t.accommodationCruiseCabin || "Cruise Cabin"}
                  </option>
                  <option value="other">{t.purposeOther || "Other"}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.notes}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Add any additional information about this segment..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            {t.cancel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {t.saving || "Saving..."}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t.save}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
